import StockService from "../../Api_Module/Api_Services/StockService";
import LoaderHelper from "../../utility/Loading/LoaderHelper";
import { useEffect, useRef, useState } from "react";
import PieChart from "../charts/PieChart";
import BalanceSheetDashboard from "../CashFlow/BalanceSheetDashboard";
import useViewportHeight from "../../helper/useViewportHeight";
import { BarChart2, RefreshCw } from "lucide-react";

interface BalanceSheetCurrentProps {
    symbol?: string;
}
interface tabsType {
    label: string;
    value: string;
}
const BalanceSheetCurrent: React.FC<BalanceSheetCurrentProps> = ({ symbol }: BalanceSheetCurrentProps) => {
    const [period, setPeriod] = useState<'annual' | 'quarter'>('annual');
    const [isLoading, setIsLoading] = useState(false);
    const [chartData, setChartData] = useState<any>([]);
    const [defaultValue, setDefaultValue] = useState<string>('0');
    const [activeTab, setActiveTab] = useState<string>('totalAssets');
    const chartAnchorRef = useRef<HTMLDivElement>(null);
    const dynamicHeight = useViewportHeight(() => chartAnchorRef.current?.getBoundingClientRect().top ?? 0, 16, 420);
    const tabs: tabsType[] = [
        { label: 'Total Assets', value: 'totalAssets' },
        { label: 'Total Liabilities', value: 'totalLiabilities' },
        { label: 'Total Equity', value: 'totalEquity' }
    ];
    useEffect(() => {
        if (symbol) {
            getCurrentBalanceSheetData();
        }
    }, [symbol, period]); // Refresh when symbol or period changes

    const getCurrentBalanceSheetData = async () => {
        try {
            setIsLoading(true);
            LoaderHelper.loaderStatus(true);
            // Use the selected period
            const result = await StockService.getBalanceSheetBreakdown(symbol, period);
            setIsLoading(false);
            LoaderHelper.loaderStatus(false);
            if (result) {
                setChartData(result);
                setDefaultValue(result[activeTab]['formattedValue']);
            }
        } catch (error) {
            setIsLoading(false);
            LoaderHelper.loaderStatus(false);
            console.error("Error fetching current balance sheet data:", error);
        }
    }

    const changeTab = (tab: string) => {
        setActiveTab(tab);
        setDefaultValue(chartData[tab] ? chartData[tab]['formattedValue'] : '0');
    }
    const getPreparedChartData = () => {
        if (!chartData || !chartData[`${activeTab}Breakdown`]) {
            return [];
        }
        let perepareChartData = chartData[`${activeTab}Breakdown`];
        let keys = Object.keys(perepareChartData);
        let data = keys.map(key => {
            if (key === "totalAssets" || key === "totalLiabilities" || key === "totalEquity") {
                return null;
            }
            if (activeTab !== "totalEquity") {
                const _keys = Object.keys(perepareChartData[key]);
                if (_keys.length === 0) {
                    return null;
                }
                return _keys.map(k => {
                    if (k != "totalCurrentAssets" && k != "totalNonCurrentAssets" && !k.endsWith("Percentage")) {
                        return {
                            name: perepareChartData[key][k]['label'],
                            y: perepareChartData[key][k]['value'],
                            formattedValue: perepareChartData[key][k]['formattedValue'],
                        };
                    } else {
                        return null;
                    }
                });
            }

        }).filter(item => item !== null);
        return (data.length > 1) ? data.flat().filter(item => item !== null) : data || [];
    }
    const isObject = (value: any) => {
        return typeof value === 'object' && value !== null && !Array.isArray(value);
    };

    const hasNestedObjects = (obj: any) => {
        if (!isObject(obj)) return false;
        return Object.values(obj).some((value) => isObject(value));
    };

    const handlePeriodChange = (newPeriod: 'annual' | 'quarter') => {
        if (newPeriod !== period) {
            setPeriod(newPeriod);
        }
    };

    return (
        <div className="bg-theme-secondary px-1 shadow-lg overflow-hidden" style={{ margin: '0px' }}>
            <div className="p-2.5 flex justify-between items-center border-b border-theme-tertiary">
                <div className="flex items-center">
                    <BarChart2 size={14} className="text-blue-400 mr-1.5" />
                    <h2 className="text-sm font-semibold text-theme-primary">
                        {chartData?.name || 'Balance Sheet Metrics'}
                    </h2>
                </div>
                <div className="flex items-center space-x-2">
                    <div className="flex items-center space-x-1">
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handlePeriodChange('annual');
                            }}
                            className={`px-2 text-xs rounded-md btn ${period === 'annual' ? 'active' : ''}`}
                            disabled={isLoading}
                        >
                            Yearly
                        </button>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handlePeriodChange('quarter');
                            }}
                            className={`px-2 text-xs rounded-md btn ${period === 'quarter' ? 'active' : ''}`}
                            disabled={isLoading}
                        >
                            Quarterly
                        </button>
                        <button 
                            onClick={(e) => {
                                e.stopPropagation();
                                getCurrentBalanceSheetData();
                            }}
                            className="p-1 text-theme-muted hover:text-theme-primary transition-colors"
                            disabled={isLoading}
                        >
                            <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
                        </button>
                    </div>
                </div>
            </div>
            <BalanceSheetDashboard balanceSheetData={chartData} />
            <div className="px-2.5 pt-0 pb-2.5 flex justify-between items-start">
                <div className="w-1/2 flex items-stretch space-x-2" style={{ height: dynamicHeight }}>
                    <div className="w-full bg-theme-secondary shadow-lg py-2 px-0 mb-0 border-theme-tertiary flex flex-col h-full overflow-hidden">
                        <div className="flex w-full  p-0.5">
                            {tabs.map((tab: tabsType) => (
                                tab.value != "totalEquity" &&
                                <button
                                    key={tab.value}
                                    onClick={() => changeTab(tab.value)}
                                    className={`
                                        flex flex-col w-1/2 items-center px-3 py-1.5 text-xs font-medium transition-colors btn
                                        ${activeTab === tab.value ? 'active' : ''}
                                    `}
                                >
                                    {chartData[tab.value] && (
                                        <h4 className="py-1">{chartData[tab.value]['formattedValue']}</h4>
                                    )}
                                    <span className="ml-1 text-xs font-normal text-theme-secondary">{tab.label}</span>
                                </button>
                            ))}
                        </div>
                        <div className="mt-2 mx-2 flex-1 overflow-y-auto custom-scrollbar">
                            {(Object.keys(chartData[`${activeTab}Breakdown`] || {}).map((dataItem: any) => {
                                if (hasNestedObjects(chartData[`${activeTab}Breakdown`][dataItem])) {
                                    return Object.keys(chartData[`${activeTab}Breakdown`][dataItem]).map((key: string, i: number) => {
                                        if (!key.endsWith("Percentage") && chartData[`${activeTab}Breakdown`][dataItem][key] && chartData[`${activeTab}Breakdown`][dataItem][key].formattedValue) {
                                            return (
                                                <div key={chartData[`${activeTab}Breakdown`][dataItem][key].name} className="flex items-center justify-between py-1">
                                                    <span className={`text-xs  text-theme-secondary py-1 ${i === 0 ? 'font-bold' : 'font-normal pl-4'}`}>{chartData[`${activeTab}Breakdown`][dataItem][key].label}</span>
                                                    <span className={`text-xs font-semibold text-theme-primary py-1`}>{chartData[`${activeTab}Breakdown`][dataItem][key].formattedValue}</span>
                                                </div>
                                            );
                                        }
                                        return null;
                                    });
                                } else {
                                    return null
                                }

                            }).filter((item: any) => item !== null))}
                        </div>
                    </div>
                </div>
                <div className="w-1/2" ref={chartAnchorRef}>
                    <PieChart defaultValue={defaultValue} data={activeTab ? getPreparedChartData() : []} height={dynamicHeight} />
                </div>
            </div>
        </div>
    )
}
export default BalanceSheetCurrent;